// src/features/profiles/components/ChangePasswordForm.jsx
import React, { useState, useRef, useEffect } from "react";
import {
    XMarkIcon,
    KeyIcon,
    EyeIcon,
    EyeSlashIcon,
} from "@heroicons/react/24/outline";

const ChangePasswordForm = ({ onSubmit, onCancel, loading }) => {
    // ANADIR current_password al estado
    const [formData, setFormData] = useState({
        current_password: "",
        new_password: "",
        re_new_password: "",
    });
  
    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false,
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const inputRefs = {
        current: useRef(null),
        new: useRef(null),
        confirm: useRef(null),
    };
  
    useEffect(() => {
        setTimeout(() => {
            if (inputRefs.current.current) {
                inputRefs.current.current.focus();
            }
        }, 100);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
        [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };
  
    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouched((prev) => ({ ...prev, [name]: true }));

        let error = "";
        if (name === "current_password" && !value) {
            error = "El password actual es requerido";
        }
        if (name === "new_password") {
            if (!value) error = "El nuevo password es requerido";
            else if (value.length < 8) error = "Min 8 caracteres";
            else if (!/[A-Z]/.test(value)) error = "Debe contener una mayuscula";
            else if (!/[0-9]/.test(value)) error = "Debe contener una cifra";
        }
        if (name === "re_new_password") {
        if (!value) error = "Confirma el nuevo password";
        else if (value !== formData.new_password)
            error = "Los password son diferentes";
        }

        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    const validateForm = () => {
        const newErrors = {};

        // Validar contrasena actual
        if (!formData.current_password) {
            newErrors.current_password = "El password actual es requerido";
        }
        // Validar nueva contrasena
        if (!formData.new_password) {
            newErrors.new_password = "El nuevo password es requerido";
        } else if (formData.new_password.length < 8) {
            newErrors.new_password = "Min 8 caracteres";
        } else if (!/[A-Z]/.test(formData.new_password)) {
            newErrors.new_password = "Debe contener una mayuscula";
        } else if (!/[0-9]/.test(formData.new_password)) {
            newErrors.new_password = "Debe contener una cifra";
        }

        // Validar confirmacion
        if (!formData.re_new_password) {
            newErrors.re_new_password = "Confirma el nuevo password";
        } else if (formData.new_password !== formData.re_new_password) {
            newErrors.re_new_password = "Los password no coinciden";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
  
    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            // Enviar TODOS los campos incluyendo current_password
            onSubmit(formData);
        }
    };
  
    const toggleShowPassword = (field) => {
        setShowPassword((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };
  
    const InputField = ({
        label,
        name,
        placeholder,
        field,
        autoFocus = false,
    }) => {
        const showField = showPassword[field];
        const error = errors[name];
        const isTouched = touched[name];
        
        return (
            <div>
                <label
                    htmlFor={name}
                    className="block text-sm font-medium text-gray-700 mb-1"
                >
                    {label} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                    <input
                        ref={autoFocus ? inputRefs.current : null}
                        type={showField ? "text" : "password"}
                        id={name}
                        name={name}
                        value={formData[name] || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={placeholder}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            error && isTouched ? "border-red-300" : "border-gray-300"
                        }`}
                        disabled={loading}
                        autoComplete="off"
                    />
                    <button
                        type="button"
                        onClick={() => toggleShowPassword(field)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        tabIndex="-1"
                    >
                        {showField ? (
                            <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                            <EyeIcon className="h-5 w-5" />
                        )}
                    </button>
                </div>
                {error && isTouched && (
                    <p className="mt-1 text-xs text-red-600">{error}</p>
                )}
            </div>
        );
    };
  
    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center pb-3 border-b">
                    <div className="flex items-center">
                        <KeyIcon className="h-6 w-6 text-blue-600 mr-2" />
                        <h3 className="text-xl font-medium text-gray-900">
                            Cambiar Password
                        </h3>
                    </div>
                    <button
                        onClick={onCancel}
                        className="text-gray-400 hover:text-gray-500"
                        disabled={loading}
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                    {/* Mensaje de seguridad */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-xs text-yellow-800">
                            <strong>Por seguridad:</strong> Debes ingresar tu password
                            actual para confirmar el cambio.
                        </p>
                    </div>

                    {/* NUEVO CAMPO: Contrasena actual */}
                    <InputField
                        label="Password actual"
                        name="current_password"
                        placeholder="Ingresa tu password actual"
                        field="current"
                        autoFocus={true}
                    />

                    <div className="border-t border-gray-200 my-2"></div>

                    {/* Nueva contrasena */}
                    <InputField
                        label="Nuevo password"
                        name="new_password"
                        placeholder="Ingresa nuevo password"
                        field="new"
                    />

                    {/* Confirmar nueva contrasena */}
                    <InputField
                        label="Confirmar password"
                        name="re_new_password"
                        placeholder="Confirma tu nuevo password"
                        field="confirm"
                    />
                    
                    {/* Indicador de requisitos */}
                    {formData.new_password && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs">
                            <p className="font-medium text-blue-800 mb-1">Requisitos:</p>
                            <ul className="space-y-1 text-blue-700">
                                <li
                                    className={`flex items-center ${formData.new_password.length >= 8 ? "text-green-600" : ""}`}
                                >
                                    <span className="mr-2">
                                        {formData.new_password.length >= 8 ? "Ok" : "Bad"}
                                    </span>
                                    Min 8 caracteres
                                </li>
                                <li
                                    className={`flex items-center ${/[A-Z]/.test(formData.new_password) ? "text-green-600" : ""}`}
                                >
                                    <span className="mr-2">
                                    {/[A-Z]/.test(formData.new_password) ? "Ok" : "Bad"}
                                    </span>
                                    Una letra mayuscula
                                </li>
                                <li
                                    className={`flex items-center ${/[0-9]/.test(formData.new_password) ? "text-green-600" : ""}`}
                                >
                                    <span className="mr-2">
                                        {/[0-9]/.test(formData.new_password) ? "Ok" : "Bad"}
                                    </span>
                                        Una cifra
                                </li>
                            </ul>
                        </div>
                    )}

                    {/* Mensaje de error general */}
                    {errors.submit && (
                        <div className="p-2 bg-red-50 border border-red-200 rounded">
                            <p className="text-xs text-red-600">{errors.submit}</p>
                        </div>
                    )}

                    {/* Botones */}
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                        >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Cambiando...
                            </>
                            ) : (
                                "Cambiar Password"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ChangePasswordForm;
